// Places the chat's streaming caret INSIDE the reply, at the end of its last line.
//
// ⚠️ The caret used to be a React element rendered AFTER `<Markdown>`:
//
//     <Markdown variant="chat">{body}</Markdown>
//     {isCursorOn && <StreamingCursor />}
//
// `Markdown` renders a block-level wrapper, and an inline-block cannot share a block's last line, so
// that caret opened an anonymous line box of its own — 24px tall at the assistant body's 16px/1.5.
// Mounting and unmounting it therefore ADDED AND REMOVED 24px of message height, and because the
// transcript is pinned to its bottom, every toggle jerked the whole chat — every settled message above
// it — by that much. Measured on the reported recording (2026-09-07) as nine one-frame ±23px jumps of
// the entire transcript inside two minutes.
//
// A CSS `::after` on the last block is the obvious fix and is NOT enough: it is only free when that
// block owns its own last line. Measured in Chrome on the real markup — paragraph 0px, heading 0px,
// code block 0px, but bulleted list +19px, ordered list +19px, blockquote +27px, table +15px, because
// `ul::after` / `blockquote::after` open a line box after the child that actually holds the last line.
// Chasing those in CSS needs a `:last-child` descent chain plus cancel rules on every container and
// every inline tag, at a specificity the cancels cannot win — a maze that rots on the next renderer
// added to `Markdown.tsx`.
//
// So the caret is placed on the tree instead, where the question "which node owns the last line?" has
// an exact answer: walk the last-child chain, descending through BLOCK elements (which hand their last
// line to a child) and stopping at text or at an inline element (which shares the line). The caret is
// appended there, inline, on a line that already exists — costing width, never height.
//
// Dependency-free by the same reasoning as `rehype-bare-autolink`: we splice into `children` ourselves.

/** The class the caret span carries. Styled once, globally (`styles/globals.css`). */
export const STREAM_CARET_CLASS = 'sym-stream-caret';

interface HastText {
  type: 'text';
  value: string;
}

interface HastElement {
  type: 'element';
  tagName: string;
  properties?: Record<string, unknown>;
  children: HastNode[];
}

interface HastParent {
  type: string;
  tagName?: string;
  children?: HastNode[];
}

type HastNode = HastText | HastElement | HastParent;

// Elements that lay their children out as blocks, so their OWN `::after`/appended child would start a
// new line rather than continue the last one. The caret is handed down through these. Everything else —
// text, `<a>`, `<strong>`, `<code>`, `<img>` — shares the line, so the caret lands beside it.
//
// `hr` is absent deliberately: it has no children to descend into, and a caret inside one does not
// render. A reply whose tail is a bare rule keeps the caret in the rule's parent — the one shape this
// cannot place inline, and the rarest.
const BLOCK_TAGS = new Set([
  'p',
  'h1',
  'h2',
  'h3',
  'h4',
  'h5',
  'h6',
  'ul',
  'ol',
  'li',
  'blockquote',
  'pre',
  'div',
  'section',
  'table',
  'thead',
  'tbody',
  'tfoot',
  'tr',
  'td',
  'th',
  'dl',
  'dd',
  'dt',
]);

function isElement(node: HastNode): node is HastElement {
  return node.type === 'element' && Array.isArray((node as HastElement).children);
}

/** ⚠️ `remark-rehype` puts a `\n` TEXT node between every pair of block children, so the last child of
 *  a `<ul>` is whitespace, not its last `<li>`. Reading `children[children.length - 1]` straight is
 *  what makes the caret land on the LIST instead of inside the item — verified against the real
 *  remark→rehype pipeline, which is where this was caught (hand-built fixtures have no such nodes,
 *  and passed). */
function lastSignificant(children: HastNode[]): HastNode | null {
  for (let i = children.length - 1; i >= 0; i--) {
    const node = children[i];
    if (node.type === 'text' && typeof (node as HastText).value === 'string' && (node as HastText).value.trim() === '') continue;
    return node;
  }
  return null;
}

/** The node the caret belongs inside: the deepest container on the last-child chain whose last child
 *  shares a line (text, or an inline element). Returns the tree itself for an empty reply. */
function tailContainer(tree: HastParent): HastParent {
  let cur: HastParent = tree;
  // Bounded rather than `while (true)`: a hast tree is finite and acyclic, but this runs on every
  // render of the live bubble and a malformed tree must not hang the chat.
  for (let depth = 0; depth < 32; depth++) {
    const kids = cur.children;
    if (!Array.isArray(kids) || kids.length === 0) return cur;
    const last = lastSignificant(kids);
    if (!last || !isElement(last)) return cur;
    // ⚠️ An EMPTY block is descended into, not stopped at. A list item exists for a beat with no text
    // in it — the `3. ` marker arrives before the first character of its content — and refusing to
    // enter it drops the caret back onto the `<ol>`, where it opens a line box under the list. That is
    // a ~24px one-frame jump of the whole transcript on every list item, which is the bug this file
    // exists to remove; it was still in the shipped build and was found in the Hebrew recording.
    // `BLOCK_TAGS` holds no void elements, so there is always somewhere to put the caret.
    //
    // A fenced block's text lives in the `<code>`, and the caret has to be in there with it — see
    // `pushCaret` for the trailing newline that otherwise pushes it onto a line of its own.
    const descend = BLOCK_TAGS.has(last.tagName) || (cur.tagName === 'pre' && last.tagName === 'code');
    if (!descend) return cur;
    cur = last;
  }
  return cur;
}

function caretNode(): HastElement {
  return {
    type: 'element',
    tagName: 'span',
    properties: { className: [STREAM_CARET_CLASS], 'aria-hidden': 'true' },
    children: [],
  };
}

/** ⚠️ Append BEFORE a trailing newline, not after it.
 *
 *  `remark-rehype` closes a fenced block as `<code>yarn build\n</code>`, and inside a `<pre>` that
 *  newline is significant — a caret pushed after it lands on a line of its own and costs the message
 *  15px, which is the bug this file exists to remove (measured in Chrome; every other tail was 0px and
 *  the code fence was the one that was not). Splitting the text node keeps the newline exactly where it
 *  was and puts the caret at the end of the last CODE line. */
function pushCaret(parent: HastParent) {
  if (!Array.isArray(parent.children)) parent.children = [];
  const kids = parent.children;
  const last = kids[kids.length - 1];
  const trailing = last && last.type === 'text' ? /\n+$/.exec((last as HastText).value ?? '') : null;
  if (last && trailing) {
    (last as HastText).value = (last as HastText).value.slice(0, -trailing[0].length);
    kids.push(caretNode(), { type: 'text', value: trailing[0] });
    return;
  }
  kids.push(caretNode());
}

/** rehype plugin: append the caret to the reply's last line. Only ever added to the plugin list for
 *  the LIVE bubble (see `Markdown`'s `caret` prop), so a settled message never carries one. */
export default function rehypeStreamingCaret() {
  return (tree: HastParent) => {
    pushCaret(tailContainer(tree));
  };
}
